import { createWebHistory, createRouter } from "vue-router";
import { ref } from 'vue';
import { msalInstance } from '@/msalInstance';

import Login from "@/views/Login.vue";
import Dashboard from "@/views/Dashboard.vue";
import DashboardNoLogin from "@/views/DashboardNoLogin.vue";
import AuthCallback from "@/views/SinglePages/AuthCallback.vue";
import Incentives from "@/views/SinglePages/Incentives.vue";
import Activity from "@/views/SinglePages/Activity.vue";
import Performance from "@/views/SinglePages/Performance.vue"; // legacy
import PerformanceRanks from "@/views/SinglePages/PerformanceRanks.vue";
import Reviews from "@/views/SinglePages/Reviews.vue";
import BusinessPartnerList from "@/views/SinglePages/BusinessPartnerList.vue";
import BusinessPartner from "@/views/SinglePages/BusinessPartner.vue";
import SalesPitchFollowUp from "@/views/SinglePages/SalesPitchFollowUp.vue";
import SalesPitchFeedback from "@/views/SinglePages/SalesPitchFeedback.vue";
import LeadGeneration from "@/views/SinglePages/LeadGeneration.vue";
import LeadMatching from "@/views/SinglePages/LeadMatching.vue";
import FunnelAnalysisTable from "@/views/SinglePages/FunnelAnalysisTable.vue";
import Settings from "@/views/Settings.vue";
import Changelog from "@/views/SinglePages/Changelog.vue";

import { useUserStore } from "@/stores/user";
import { useAuthStore } from "@/stores/auth";
import { useBusinessPartnersStore } from '@/stores/businessPartners'
import { useLeadGenerationStore } from '@/stores/leadGeneration'
import { useAdminStore } from "@/stores/admin";

const msalReady = (async () => {
    await msalInstance.initialize();
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
        msalInstance.setActiveAccount(response.account);
    } else {
        const [existing] = msalInstance.getAllAccounts();
        if (existing) msalInstance.setActiveAccount(existing);
    }
})();

const rememberMe = localStorage.getItem('rememberMe')

const routes = [

    {
        path: import.meta.env.BASE_URL,
        name: "Login",
        component: Login,
        meta: {
            requiresLogin: false
        }
    },
    {
        path: '/auth/callback',
        name: "AuthCallback",
        component: AuthCallback,
        meta: {
            requiresLogin: false
        }
    },
    {
        name: "DashboardNoLogin",
        path: `/noLogin`,
        component: Dashboard,
        meta: {
            requiresLogin: false
        }
    },
    {
        name: "Dashboard",
        path: `/dashboard`,
        component: Dashboard,
        meta: {
            requiresLogin: true
        }
    },
    {
        name: "Incentives",
        path: `/incentives`,
        component: Incentives,
        meta: {
            requiresLogin: true
        }
    },
    {
        name: "Activity",
        path: `/activity`,
        component: Activity,
        meta: {
            requiresLogin: true
        }
    },
    {
        name: "Performance",
        path: `/performance`,
        component: PerformanceRanks,
        meta: { requiresLogin: true }
    },
    {
        name: "PerformanceLegacy",
        path: `/performance-legacy`,
        component: Performance,
        meta: { requiresLogin: true }
    },
    {
        name: "SalesPitchFollowUp",
        path: `/salesPitchFollowUp`,
        component: SalesPitchFollowUp,
        meta: {
            requiresLogin: true
        }
    },
    {
        name: "SalesPitchFeedback",
        path: `/salesPitchFeedback`,
        component: SalesPitchFeedback,
        meta: {
            requiresLogin: true
        }
    },
    {
        name: "BusinessPartnerList",
        path: `/businessPartnerList`,
        component: BusinessPartnerList,
        meta: {
            requiresLogin: true
        }
    },
    {
        name: "LeadGeneration",
        path: `/leadgeneration`,
        component: LeadGeneration,
        meta: {
            requiresLogin: true
        }
    },
    {
        name: "LeadMatching",
        path: `/leadmatching`,
        component: LeadMatching,
        meta: {
            requiresLogin: true
        }
    },
    {
        name: "BusinessPartner",
        path: `/businessPartner`,
        component: BusinessPartner,
        meta: {
            requiresLogin: true
        }
    },
    {
        name: "Reviews",
        path: `/reviews`,
        component: Reviews,
        meta: {
            requiresLogin: true
        }
    },
    {
        name: "Settings",
        path: `/settings`,
        component: Settings,
        meta: { requiresLogin: true }
    },
    {
        name: "Changelog",
        path: `/changelog`,
        component: Changelog,
        meta: { requiresLogin: true }
    },
    {
        name: "FunnelAnalysis",
        path: `/funnelAnalysis`,
        component: FunnelAnalysisTable,
        meta: { requiresLogin: true, requiresManager: true }
    },
];

async function navigateTo(to, from, next) {
    const businessPartnersStore = useBusinessPartnersStore();
    const businessPartnersListsStore = useBusinessPartnersStore();
    const leadGenerationStore = useLeadGenerationStore();
    const userStore = useUserStore();
    userStore.currentSelection.module = to.name;

    // set the searchError to false to prevent showing the error message from the previous searches
    userStore.currentSelection.businessPartnerList.search.error = false;

    let searchResult = null;
    switch (to.name) {
        case "LeadGeneration":
            userStore.currentSelection.leadGeneration.signature = to.query.signature;
            next();
            break;
        case "LeadMatching":
            userStore.currentSelection.leadMatching.signature = to.query.signature;
            next();
            break;
        case "BusinessPartner":
            if (to.query.source) userStore.currentSelection.businessPartnerList.source = to.query.source;
            userStore.currentSelection.businessPartnerList.search.mode = to.query.searchMode;
            userStore.currentSelection.businessPartnerList.search.query = to.query.search;
            // neue Flags übernehmen (wholeWord & matchCase) falls vorhanden
            if (to.query.wholeWord !== undefined) {
                userStore.currentSelection.businessPartnerList.search.wholeWord = ['1', 'true', 'True'].includes(String(to.query.wholeWord));
            }
            if (to.query.matchCase !== undefined) {
                userStore.currentSelection.businessPartnerList.search.matchCase = ['1', 'true', 'True'].includes(String(to.query.matchCase));
            }
            const fromList = to.query.fromList === 'true';
            const mode = userStore.currentSelection.businessPartnerList.search.mode;
            const queryValue = userStore.currentSelection.businessPartnerList.search.query;
            // Spezialfall: Klick aus der vorhandenen Liste auf einen Eintrag (GPNo Suche) => keine neue Suche auslösen, Liste behalten
            if (fromList && mode === 'GPNo' && businessPartnersStore.businessPartnerList?.data?.length) {
                const existing = businessPartnersStore.businessPartnerList.data.find(bp => String(bp.gpNo) === String(queryValue));
                if (existing) {
                    userStore.currentSelection.businessPartner.id = existing.id;
                    // Load full detail payload without replacing the active list/search context.
                    try {
                        await businessPartnersStore.loadDetailByGpNo(existing.gpNo, existing.id);
                    } catch (e) {
                        // Ignore here and keep navigation responsive; fallback path below still handles missing data.
                    }
                    // Markiere als gesehen ohne Liste zu ersetzen
                    existing.unseen = 0;
                    next();
                    return;
                }
            }
            // Fallback: reguläre Suche
            searchResult = await businessPartnersStore.getBySearch();
            // console.log('search with', to.query.search, 'returned result', searchResult);

            if (!searchResult) {
                // the search was not successful
                userStore.currentSelection.businessPartnerList.search.error = 'NO RESULTS'
                // console.log('search error', userStore.currentSelection.businessPartnerList.search.error);
                next(false);
                return;
            }
            // Bei GPNo-Suche bevorzugen wir immer den exakten Treffer,
            // auch wenn der Backend-Search mehrere Datensätze zurückliefert.
            if (mode === 'GPNo' && searchResult && Array.isArray(searchResult.data) && searchResult.data.length) {
                const exactGpNoHit = searchResult.data.find((bp) => String(bp?.gpNo || bp?.gp?.no || '') === String(queryValue));
                if (exactGpNoHit) {
                    userStore.currentSelection.businessPartner.id = exactGpNoHit.id;
                    const seenBusinessPartner = businessPartnersStore.businessPartnerList?.data?.find((businessPartner) => businessPartner.id === exactGpNoHit.id);
                    if (seenBusinessPartner) seenBusinessPartner.unseen = 0;
                    next();
                    return;
                }
            }
            if (searchResult && Array.isArray(searchResult.data) && searchResult.size === 1) {
                // the search returned exactly one businessPartner => route to the businessPartner details page and set the businessPartner to unseen = 0
                userStore.currentSelection.businessPartner.id = searchResult.data[0].id;
                const seenBusinessPartner = businessPartnersStore.businessPartnerList?.data?.find((businessPartner) => businessPartner.id === searchResult.data[0].id);
                if (seenBusinessPartner) seenBusinessPartner.unseen = 0;
                // console.log('set to seen', searchResult.data[0].name, 'id', searchResult.data[0].id, seenBusinessPartner);
                // console.log('search with one result', searchResult.data[0].name, 'id', searchResult.data[0].id, 'source', userStore.currentSelection.businessPartnerList.source, 'searchResult', searchResult);
                next();
                return;
            }
            if (searchResult && Array.isArray(searchResult.data) && searchResult.size > 1) {
                // the search returned more then one businessPartner => route to the businessPartner list to allow for selecting the right businessPartner
                // console.log('search with multiple results', searchResult.size, 'source', userStore.currentSelection.businessPartnerList.source);
                next({ name: "BusinessPartnerList" });
                return;
            }
            // 0 results or unexpected shape: stay on current page rather than showing an empty list
            userStore.currentSelection.businessPartnerList.search.error = 'NO RESULTS';
            next(false);
            return;
        case "BusinessPartnerList":
            if (to.query.source) userStore.currentSelection.businessPartnerList.source = to.query.source;
            console.log('[BP_LIST_ROUTER] enter BusinessPartnerList', {
                source: userStore.currentSelection.businessPartnerList.source,
                rawFilter: to.query.filter,
                namedFilter: to.query.namedFilter,
                search: to.query.search,
                wholeWord: to.query.wholeWord,
                isUserFilter: to.query.isUserFilter,
            });
            if (userStore.currentSelection.businessPartnerList.source === 'search') {
                // Wenn direkt auf die Liste mit einer Suchanfrage navigiert wird (z.B. aus PreviewBusinessPartnerList),
                // wurden bisher searchMode und search.query nicht gesetzt, wodurch der Backend-Call ein leeres "search" erhielt.
                // Gleiches Verhalten wie im Case "BusinessPartner" herstellen.
                if (to.query.searchMode !== undefined) {
                    userStore.currentSelection.businessPartnerList.search.mode = to.query.searchMode;
                }
                if (to.query.search !== undefined) {
                    userStore.currentSelection.businessPartnerList.search.query = to.query.search;
                }
                // console.log('getListByFilter router: fetching with search', userStore.currentSelection.businessPartnerList.search.mode, userStore.currentSelection.businessPartnerList.search.query)
                if (to.query.wholeWord !== undefined) {
                    userStore.currentSelection.businessPartnerList.search.wholeWord = ['1', 'true', 'True'].includes(String(to.query.wholeWord));
                }
                if (to.query.matchCase !== undefined) {
                    userStore.currentSelection.businessPartnerList.search.matchCase = ['1', 'true', 'True'].includes(String(to.query.matchCase));
                }
                searchResult = await businessPartnersStore.getBySearch();
            }
            userStore.currentSelection.businessPartnerList.filter.show = (!to.query.hideFilter) ? true : false;
            if (userStore.currentSelection.businessPartnerList.source === 'filter') {
                // If navigating generically to the list (no explicit filter/namedFilter), clear stale search
                const hasExplicitFilter = !!to.query.filter || !!to.query.namedFilter;
                if (!hasExplicitFilter) {
                    try {
                        if (userStore.currentSelection?.businessPartnerList?.filter) {
                            userStore.currentSelection.businessPartnerList.filter.search = null;
                            // Ensure no default status is preselected on generic list open
                            userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerStatus = null;
                        }
                        if (userStore.currentSelection?.businessPartnerList?.search) {
                            userStore.currentSelection.businessPartnerList.search.query = '';
                            userStore.currentSelection.businessPartnerList.search.string = '';
                        }
                    } catch (e) { /* noop */ }
                }
                let filter = null;
                let searchCriteria = null;
                const rawFilter = to.query.filter;
                // Unterstützt sowohl Signatur-Strings (pipe-getrennt) als auch JSON-basierte Filter
                if (!to.query.namedFilter && typeof rawFilter === 'string' && rawFilter.trim().startsWith('{')) {
                    // JSON-Filter aus der URL: in den Store übernehmen und über currentFilter in Filter + searchCriteria auflösen
                    try {
                        const parsed = JSON.parse(rawFilter);
                        userStore.currentSelection.businessPartnerList.filter = parsed;
                        const cf = userStore.currentFilter;
                        filter = cf.filter;
                        searchCriteria = cf.searchCriteria;
                        console.log('[BP_LIST_ROUTER] JSON filter from route, currentFilter snapshot', {
                            signature: cf.filter?.signature,
                            salesValues: cf.filter?.salesValues,
                            businessPartnerSalesPitchStatus: cf.filter?.businessPartnerSalesPitchStatus,
                            invoice: cf.filter?.invoice,
                        });
                    } catch (e) {
                        console.warn('Konnte JSON-Filter aus Route nicht parsen, falle auf Store-Filter zurück:', e);
                        const cf = userStore.currentFilter;
                        filter = cf.filter;
                        searchCriteria = cf.searchCriteria;
                    }
                } else if (!to.query.namedFilter) {
                    for (const item of (typeof rawFilter === 'string' ? rawFilter.split('|') : [])) {
                        // Split only on first colon so comma-separated PLZ IDs remain intact
                        const colonIdx = item.indexOf(':');
                        if (colonIdx === -1) continue;
                        const key = item.slice(0, colonIdx);
                        const value = item.slice(colonIdx + 1);
                        if (key === 'idBusinessPartnerStatus') {
                            const maybeNum = Number(value);
                            userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerStatus = Number.isNaN(maybeNum) ? value : maybeNum;
                        } else if (key === 'idBusinessPartnerCityZip') {
                            // Restore PLZ selection so filter UI shows the correct value after navigation back
                            const maybeNum = Number(value);
                            const restored = Number.isNaN(maybeNum) ? value : maybeNum;
                            if (restored !== null && restored !== undefined && restored !== 'undefined' && restored !== 'null') {
                                userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerCityZip = restored;
                            }
                        } else if (key === 'nameBusinessPartnerSector') {
                            // Restore Branche selection so filter UI shows the correct value after navigation back
                            if (value && value !== 'undefined' && value !== 'null') {
                                userStore.currentSelection.businessPartnerList.filter.nameBusinessPartnerSector = value;
                            }
                        }
                        // Note: search is included in userStore.currentFilter via currentSelection.filter.search and does not need parsing here
                    }
                    const cf = userStore.currentFilter;
                    filter = cf.filter;
                    searchCriteria = cf.searchCriteria;
                    console.log('[BP_LIST_ROUTER] signature/string filter, currentFilter snapshot', {
                        signature: cf.filter?.signature,
                        salesValues: cf.filter?.salesValues,
                        businessPartnerSalesPitchStatus: cf.filter?.businessPartnerSalesPitchStatus,
                        invoice: cf.filter?.invoice,
                    });
                } else {
                    filter = businessPartnersStore.namedFilter(to.query.namedFilter)
                    searchCriteria = []
                    // console.log('getListByFilter router: fetching with named filter', to.query.namedFilter, ":", filter)
                }
                // Wenn bereits eine Liste mit dieser Signatur geladen wurde, verwende exakt den dort gespeicherten Filter
                // (inkl. salesValues, businessPartnerSalesPitchStatus, invoice, searchCriteria),
                // statt den Filter jedes Mal aus dem UI-Status neu zusammenzubauen.
                if (filter && filter.signature) {
                    const existingList = businessPartnersStore.businessPartnerLists
                        ? businessPartnersStore.businessPartnerLists.find((list) => list.signature === filter.signature)
                        : null;
                    console.log('[BP_LIST_ROUTER] before reuse check', {
                        signature: filter.signature,
                        hasExistingList: !!existingList,
                        initialSalesValues: filter.salesValues,
                        initialInvoice: filter.invoice,
                        initialBpsStatus: filter.businessPartnerSalesPitchStatus,
                        initialSearchCriteria: searchCriteria,
                    });
                    if (existingList && existingList.filter) {
                        try {
                            filter = JSON.parse(JSON.stringify(existingList.filter));
                        } catch (e) {
                            filter = existingList.filter;
                        }
                        if (Array.isArray(existingList.searchCriteria)) {
                            try {
                                searchCriteria = JSON.parse(JSON.stringify(existingList.searchCriteria));
                            } catch (e) {
                                searchCriteria = existingList.searchCriteria;
                            }
                        }
                        console.log('[BP_LIST_ROUTER] reusing stored filter for signature', filter.signature, {
                            reusedSalesValues: filter.salesValues,
                            reusedInvoice: filter.invoice,
                            reusedBpsStatus: filter.businessPartnerSalesPitchStatus,
                            reusedSearchCriteria: searchCriteria,
                        });
                    }
                }
                console.log('[BP_LIST_ROUTER] calling getByFilter with', {
                    finalSignature: filter && filter.signature,
                    finalSalesValues: filter && filter.salesValues,
                    finalInvoice: filter && filter.invoice,
                    finalBpsStatus: filter && filter.businessPartnerSalesPitchStatus,
                    finalSearchCriteria: searchCriteria,
                });
                // Nach erfolgreichem Request: lastAppliedSignature und isUserFilter im userStore setzen
                if (filter && filter.signature) {
                    userStore.currentSelection.businessPartnerList.lastAppliedSignature = filter.signature;
                }
                // Option userApplied: true für explizite User-Filter setzen
                // const isUserApplied = userStore.currentSelection.businessPartnerList.source === 'filter';
                const isUserApplied = to.query.isUserFilter === 'true';
                searchResult = await businessPartnersStore.getByFilter(filter, searchCriteria, false, { userApplied: isUserApplied });
            }
            next();
            break;
        case "SalesPitchFeedback":
            await businessPartnersListsStore.getByFilter(businessPartnersListsStore.namedFilter('ToDo:VorOrtTermine'), [], true)
            next();
            break;
        case "SalesPitchFollowUp":
            await businessPartnersListsStore.getByFilter(businessPartnersListsStore.namedFilter('ToDo:Wiedervorlagen'), [], true)
            next();
            break;
        default:
            next();
    }
}

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (to.hash) {
            return {
                el: to.hash,
                behavior: 'smooth',
                top: 120,
            };
        } else if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0 };
        }
    },
});

router.beforeEach(async (to, from, next) => {
    await msalReady;
    const userStore = useUserStore();
    const authStore = useAuthStore();
    const adminStore = useAdminStore();

    await authStore.setActiveFromCache();

    if (!authStore.accessToken) {
        console.log('[ROUTER] Kein Token vorhanden, Weiterleitung zur Login-Seite');
        if (to.name !== "Login") {
            console.log('[ROUTER] Weiterleitung zur Login-Seite');
            next({ name: "Login" });
            return;
        } else {
            console.log('[ROUTER] Bereits auf Login-Seite, Navigation fortsetzen');
            next();
            return
        }
    } else if (!userStore.id && !adminStore.isAdmin) {
        console.log('[ROUTER] Token vorhanden, aber kein User eingeloggt. Silent Login wird versucht mit:', authStore.account?.username);
        await userStore.login(authStore.account?.username);
        console.log('[ROUTER] Silent Login Ergebnis:', authStore.account?.username, userStore.loginStatus, 'id:', userStore.id, 'isAdmin:', adminStore.isAdmin);

        // Für normale User muss ein gültiger User geladen sein;
        // für Admin-SSO (nur Kollegenliste) reicht loginStatus === success.
        if (userStore.loginStatus !== "success" || (!userStore.id && !adminStore.isAdmin)) {
            console.log('[ROUTER] Silent Login fehlgeschlagen, Weiterleitung zur Login-Seite');
            next({ name: "Login" });
            return;
        }

        console.log('[ROUTER] Silent Login erfolgreich, Weiterleitung zum Dashboard');
        next({ name: "Dashboard" });
        return;
    } else if (adminStore.isAdmin && !adminStore.loginAs) {
        console.log('[ROUTER] User ist admin nach login:', authStore.accessToken, userStore.id);
        next();
        return;
    } else {
        console.log('[ROUTER] User ist normaler  user:', authStore.accessToken, userStore.id, 'adminStore.loginAs:', adminStore.loginAs);
        // Manager-only route guard (lightweight). If user lacks management role, redirect.
        if (to.name === 'FunnelAnalysis' && !(userStore.isManagement || userStore.isSalesTeamManager || userStore.isWinback)) {
            console.log('[ROUTER] Zugriff auf FunnelAnalysis verweigert – keine Management-Rolle');
            next({ name: 'Dashboard' });
            return;
        }
        // Herkunft für BusinessPartner Details merken, damit navigateBack exakt zur Ursprungs-Seite führt
        if (to.name === 'BusinessPartner') {
            try {
                userStore.currentSelection.businessPartner.origin = {
                    name: from.name,
                    path: from.path,
                    query: { ...(from.query || {}) },
                    hash: from.hash || ''
                };
            } catch (e) { /* ignore */ }
        }
        await navigateTo(to, from, next);
        return;
    }

});


export default router;